import { Component, ElementRef, Injector, OnDestroy, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AbstractComponent, Attendance, AttendanceMessageHit, AttendanceService, AttendanceStatusEnum, AttendanceWhisperAlert, AttendanceWhisperService, CacheService, Channel, CountState, Department, IntegrationTypeEnum, LoadMessageService, Message, Pager, SocketService, TabService, Tag, User, WHISPER_ALERT_SOCKET_EVENT, unreadWeight } from 'lib-trend-core';
import { Observable, Subject, combineLatest, concatMap, debounceTime, distinctUntilChanged, map, of, switchMap, takeUntil, tap } from 'rxjs';
import { ContactEditComponentForAttendance } from '../attendance-panel-components/contact/contact-edit/contact-edit.component';
import { ContactNewConversationComponent } from '../attendance-panel-components/new-conversation/new-conversation.component';
import { addWhisperPending, adjustUnreadCount, clearMessageHits, focusMessage, getAttendance, getAttendances, getCounts, getIntegrations, getMessageHits, getPinnedAttendances, getSimpleLists, getTags, getWhisperPending, openCloseSidebarRight, removeWhisperPending, setAttendance, setMessages } from '../state/actions';
import { AppState, MessageHitsState, PagerParamsState } from '../state/app.state';
import { attendancesSelector, countsSelector, countsWithMessagesSelector, messageHitsSelector, pinnedAttendancesSelector, simpleChannelsSelector, simpleDepartmentsSelector, simpleTagsSelector, simpleUsersSelector } from '../state/selectors';

export interface AttendanceSocketUpdateData {
  attendance: Attendance,
}

@Component({
    selector: 'attendance-panel-list-component',
    templateUrl: 'attendance-panel-list.v2.component.html',
    // templateUrl: 'attendance-panel-list.component.html',
    styleUrls: ['./attendance-panel-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttendancePanelList extends AbstractComponent implements OnInit, OnDestroy {

  public readonly pinnedAttendances$: Observable<Array<Attendance>> = this.store.select(pinnedAttendancesSelector);
  public readonly pager$: Observable<Pager<Attendance>> = this.store.select(attendancesSelector);
  public readonly counts$: Observable<Array<CountState>> = this.store.select(countsSelector);
  public readonly countsWithMessages$: Observable<Array<CountState>> = this.store.select(countsWithMessagesSelector);
  public readonly messageHits$: Observable<MessageHitsState> = this.store.select(messageHitsSelector);
  // Attendants and supervisors only filter by the channels of their own departments — the same
  // rule as the channel picker of a new conversation.
  public readonly listChannel$: Observable<Array<Channel>> = combineLatest([
    this.store.select(simpleChannelsSelector),
    this.store.select(simpleDepartmentsSelector),
  ]).pipe(
    map(([channels, departments]) => {
      if (!this.isAttendant() && !this.isSupervisor()) {
        return channels;
      }
      const idDepartmentsUser = this.getIDDepartmentsUserLogged() ?? [];
      const idChannelsUser = (departments ?? [])
        .filter((department: Department) => idDepartmentsUser.includes(department._id))
        .map((department: Department) => department.channel?.toString());
      return (channels ?? []).filter((channel: Channel) => idChannelsUser.includes(channel._id));
    }),
  );
  public readonly listTag$: Observable<Array<Tag>> = this.store.select(simpleTagsSelector);
  public readonly listDepartment$: Observable<Array<Department>> = this.store.select(simpleDepartmentsSelector);
  public readonly listUser$: Observable<Array<User>> = this.store.select(simpleUsersSelector);
  listDepartmentFiltered$: Observable<Array<Department>>;

  @ViewChild('btnUpdateAttendancesFromSocket') btnUpdateAttendancesFromSocket: ElementRef;
  @ViewChild('btnUpdateRefreshAttendancesFromSocket') btnUpdateRefreshAttendancesFromSocket: ElementRef;
  @ViewChild('searchBox') searchBox: ElementRef<HTMLInputElement>;

  selectedIndex: number = 0;
  DEFAULT_PER_PAGE_ATTENDANCES: number = Number(this.environment['defaultPerPageAttendances']);
  pager: Pager<Attendance> = new Pager<Attendance>({ perPage: this.DEFAULT_PER_PAGE_ATTENDANCES });
  pinnedAttendances: Array<Attendance> = new Array<Attendance>();

  selectedDepartmentIds: string[] = [];
  selectedChannelIds: string[] = [];
  selectedTagIds: string[] = [];
  selectedUserIds: string[] = [];
  unreadMessages: boolean = false;

  labelDepartment: string = 'Departamentos';
  labelChannel: string = 'Canais';
  labelTag: string = 'Tags';

  private notificationAudio = new Audio('assets/audio/alert-message.mp3');
  private listObservable: Observable<void>;
  private termoDaBusca: Subject<string> = new Subject<string>();

  overtimeCount: number = 0;
  pendingCount: number = 0;
  inProgressCount: number = 0;
  pausedCount: number = 0;
  closedCount: number = 0;
  readonly STATUS = AttendanceStatusEnum;
  unreadByStatus: Partial<Record<AttendanceStatusEnum, number>> = {};
  attendance: Attendance;
  messageHits: MessageHitsState;

  pagerParamsState: PagerParamsState;
  idAttendanceParam!: string;

  loadingMore: boolean = false;

  blockSocketWhenFilterIsActive: boolean = false;

  constructor(
    injector: Injector,
    private store: Store<AppState>,
    private loadMessageService: LoadMessageService,
    private tabService: TabService,
    public dialog: MatDialog,
    public socketService: SocketService,
    public attendanceService: AttendanceService,
    public cacheService: CacheService,
    private whisperService: AttendanceWhisperService,
  ) {
    super(injector);
    this.createForm();
    this.getAttendanceByParam();
  }

  private normalizeStatusToCardFormat(status: string): string {
    if (!status) return 'global';

    switch (status.toUpperCase()) {
      case 'OVERTIME': return 'overtime';
      case 'PENDING': return 'wating';
      case 'IN_PROGRESS': return 'progress';
      case 'PAUSED': return 'paused';
      case 'CLOSED': return 'closed';
      default: return status.toLowerCase();
    }
  }

  ngOnInit(): void {
    this.formGroup.controls['status'].valueChanges.subscribe(() => {
      this.getList();
    });

    this.getList();
    this.setupObservableSearch();
    this.store.dispatch(getWhisperPending());

    this.tabService.indexTab$
      .pipe(takeUntil(this.destroy$))
      .subscribe((index) => {
        this.selectedIndex = index;
      });

    this.formGroup.controls['department'].valueChanges.subscribe((department: Department | null) => {
      this.searchByDepartment(department);
    });

    this.formGroup.controls['channel'].valueChanges.subscribe((channel: Channel | null) => {
      this.searchByChannel(channel);
    });

    this.formGroup.controls['tags'].valueChanges.subscribe((tag: Tag | null) => {
      this.searchByTag(tag);
    });

    this.formGroup.controls['unreadAttendance'].valueChanges.subscribe((unreadAttendance: boolean) => {
      this.filterByUnreadMessages(unreadAttendance);
    });

    this.pinnedAttendances$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pinnedAttendances: Array<Attendance>) => {
        this.pinnedAttendances = pinnedAttendances;
      });

    this.pager$
      .pipe(takeUntil(this.destroy$))
      .subscribe((pager: Pager<Attendance>) => {
        if (this.loadingMore) {
          const merged = [...this.pager.list, ...pager.list];
          const list = Array.from(new Map(merged.map((item) => [item._id, item])).values());
          this.pager = { ...this.pager, page: pager.page, total: pager.total, perPage: pager.perPage, list };
          this.loadingMore = false;
        } else {
          this.pager = pager;
          this.store.dispatch(getPinnedAttendances({ status: this.formGroup.get('status')?.value }));
        }
        this.loading = false;
      });

    this.counts$
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts: Array<CountState>) => {
        this.loading = false;
        this.checkAndPlayNotificationSound(counts);
      });

    // Displayed counters also include the attendances found only through a message.
    this.countsWithMessages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts: Array<CountState>) => this.setValuesCountsAttendance(counts));

    this.messageHits$
      .pipe(takeUntil(this.destroy$))
      .subscribe((messageHits: MessageHitsState) => this.messageHits = messageHits);

    this.loadMessageService.eventLoadMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.loading = result;
      });

    this.configSocket();

    // load the integrations to store - type PIPEDRIVE
    this.store.dispatch(getIntegrations({ ptype: IntegrationTypeEnum.PIPEDRIVE }));

    // load the simple lists: channels, departments, tags and users
    this.store.dispatch(getSimpleLists({ idCompany: this.getIDCurrentCompany() }));
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getAttendanceByParam(): void {
    this.route.queryParams.subscribe(params => {
      const id = params['id'];
      const status = params['status'];

      this.idAttendanceParam = id;

      if (id) {
        this.store.dispatch(getAttendance({ idAttendance: id }));

        switch (!!status) {
          case status === AttendanceStatusEnum.PENDING: {
            this.setTabIndexAndStatus(1, status);
            break;
          }
          case status === AttendanceStatusEnum.IN_PROGRESS: {
            this.setTabIndexAndStatus(2, status);
            break;
          }
          case status === AttendanceStatusEnum.PAUSED: {
            this.setTabIndexAndStatus(3, status);
            break;
          }
          case status === AttendanceStatusEnum.CLOSED: {
            this.setTabIndexAndStatus(4, status);
            break;
          }
          default: {
            this.setTabIndexAndStatus(0, status);
            break;
          }
        }
      }
    });
  }

  private setTabIndexAndStatus(index: number, status: AttendanceStatusEnum): void {
    this.tabService.selectedTabIndex(index);
    this.formGroup.get('status').setValue(status, { emitEvent: false });
  }

  private createForm(): void {
    this.formGroup = this.formBuilder.group({
      status: [AttendanceStatusEnum.OVERTIME],
      department: [[]],
      channel: [[]],
      tags: [[]],
      unreadAttendance: [false],
    });
  }

  private setupObservableSearch() {
    this.listObservable = this.termoDaBusca.pipe(
      takeUntil(this.destroy$),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        this.loading = true;
        this.store.dispatch(getCounts({ search: term, params: this.searchParams }));
        this.store.dispatch(getAttendances({ page: 1, perPage: this.pager.perPage, search: term, params: this.searchParams }));
        this.dispatchMessageHits(term);
        return of(null);
      })
    );

    this.listObservable.subscribe({
      next: () => {
        this.loading = false;
        this.updateBlockSocketFlag();
      },
      error: (error) => {
        this.loading = false;
      }
    });
  }

  search(termo: string) {
    this.loading = true;
    this.searchString = termo;
    this.termoDaBusca.next(termo);
    this.updateBlockSocketFlag();
  }

  private updateBlockSocketFlag(): void {
    this.blockSocketWhenFilterIsActive = (
      this.selectedDepartmentIds.length > 0 ||
      this.selectedChannelIds.length > 0 ||
      this.selectedTagIds.length > 0 ||
      this.selectedUserIds.length > 0 ||
      this.unreadMessages ||
      (!!this.searchString)
    );
  }

  private filterData() {
    this.loading = true;
    this.searchParams = {
      company: this.getIDCurrentCompany(),
      status: this.formGroup.get('status')?.value,
      department: this.selectedDepartmentIds,
      channel: this.selectedChannelIds,
      tags: this.selectedTagIds,
      user: this.selectedUserIds,
      closedLast24hours: 'true'
    };

    if (this.unreadMessages) {
      this.searchParams.unreadAttendance = 'true';
    }

    this.updateBlockSocketFlag();
    this.store.dispatch(getCounts({ search: this.searchString, params: this.searchParams }));
    this.store.dispatch(getAttendances({
      page: 1,
      perPage: this.pager.perPage,
      search: this.searchString,
      params: this.searchParams,
    }));
    this.dispatchMessageHits(this.searchString);
  }

  // Message search only from 3 characters; below that the section is cleared.
  private dispatchMessageHits(term: string): void {
    const search = (term ?? '').trim();
    if (search.length >= 3) {
      this.store.dispatch(getMessageHits({ search, page: 1, params: this.searchParams }));
    } else {
      this.store.dispatch(clearMessageHits());
    }
  }

  loadMoreMessageHits(): void {
    this.store.dispatch(getMessageHits({ search: this.messageHits.search, page: this.messageHits.page + 1, params: this.messageHits.params }));
  }

  retryMessageHits(): void {
    this.dispatchMessageHits(this.messageHits.search);
  }

  get hasMessageResults(): boolean {
    return !!this.messageHits && (this.messageHits.hits.length > 0 || this.messageHits.loading || this.messageHits.error);
  }

  openMessageHit(hit: AttendanceMessageHit): void {
    this.clearAttendanceParam();
    if (this.attendance?._id !== hit.attendanceId) {
      this.attendance = <Attendance>{ _id: hit.attendanceId };
      this.store.dispatch(setMessages({ messages: new Pager<Message>() }));
      this.store.dispatch(getAttendance({ idAttendance: hit.attendanceId }));
      this.store.dispatch(openCloseSidebarRight({ sidebarRight: false }));
      this.loadMessageService.setLoadingMessage(true);
    }
    this.store.dispatch(focusMessage({ focus: { attendanceId: hit.attendanceId, messageId: hit.messageId } }));
  }

  searchByDepartment(department: Department | null): void {
    if (department) {
      if (this.selectedDepartmentIds.includes(department._id)) {
        this.selectedDepartmentIds = this.selectedDepartmentIds.filter(id => id !== department._id);
      } else {
        this.selectedDepartmentIds = [...this.selectedDepartmentIds, department._id];
      }
    } else {
      this.selectedDepartmentIds = [];
    }
    this.filterData();
  }

  searchByChannel(channel: Channel | null): void {
    if (channel) {
      if (this.selectedChannelIds.includes(channel._id)) {
        this.selectedChannelIds = this.selectedChannelIds.filter(id => id !== channel._id);
      } else {
        this.selectedChannelIds = [...this.selectedChannelIds, channel._id];
      }
      this.listDepartmentFiltered$ = this.listDepartment$.pipe(
        takeUntil(this.destroy$),
        switchMap((departments: Array<Department>) => {
          if (this.selectedChannelIds.length === 0) {
            return of([]);
          }
          const filtered = departments.filter((department: Department) =>
            this.selectedChannelIds.includes(department.channel?.toString())
          );
          return of(filtered);
        })
      );
    } else {
      this.selectedChannelIds = [];
    }
    this.filterData();
  }

  searchByTag(tag: Tag | null): void {
    if (tag) {
      if (this.selectedTagIds.includes(tag._id)) {
        this.selectedTagIds = this.selectedTagIds.filter(id => id !== tag._id);
      } else {
        this.selectedTagIds = [...this.selectedTagIds, tag._id];
      }
    } else {
      this.selectedTagIds = [];
    }
    this.filterData();
  }

  searchByUser(user: User | null): void {
    if (user) {
      if (this.selectedUserIds.includes(user._id)) {
        this.selectedUserIds = this.selectedUserIds.filter(id => id !== user._id);
      } else {
        this.selectedUserIds = [...this.selectedUserIds, user._id];
      }
    } else {
      this.selectedUserIds = [];
    }
    this.filterData();
  }

  filterByUnreadMessages(unreadAttendance: boolean | null): void {
    if (this.unreadMessages !== unreadAttendance) {
      this.unreadMessages = unreadAttendance;
      this.filterData();
    }
  }

  clearFilters(): void {
    this.selectedDepartmentIds = [];
    this.selectedChannelIds = [];
    this.selectedUserIds = [];
    this.selectedTagIds = [];
    this.searchString = '';
    this.unreadMessages = false;
    this.listDepartmentFiltered$ = of([]);

    if (this.searchBox && this.searchBox.nativeElement) {
      this.searchBox.nativeElement.value = '';
    }

    this.filterData();
  }

  private getList(): void {
    this.loading = true;

    this.searchParams = {
      company: this.getIDCurrentCompany(),
      status: this.formGroup.get('status')?.value,
      department: this.selectedDepartmentIds,
      channel: this.selectedChannelIds,
      tags: this.selectedTagIds,
      user: this.selectedUserIds,
      unreadAttendance: this.unreadMessages,
      closedLast24hours: true
    };

    this.updateBlockSocketFlag();

    this.store.dispatch(getCounts({ search: this.searchString, params: this.searchParams }));
    this.store.dispatch(getAttendances({
      page: this.pager.page,
      perPage: this.pager.perPage,
      search: this.searchString,
      params: this.searchParams,
    }));
    this.dispatchMessageHits(this.searchString);
    this.store.dispatch(getTags({ idCompany: super.getIDCurrentCompany() }));
  }

  changeTab(event: any) {
    this.pager = new Pager<Attendance>({ perPage: this.DEFAULT_PER_PAGE_ATTENDANCES });
    let newStatus = AttendanceStatusEnum.OVERTIME;
    switch (event.index) {
      case 1:
        newStatus = AttendanceStatusEnum.PENDING;
        break;
      case 2:
        newStatus = AttendanceStatusEnum.IN_PROGRESS;
        break;
      case 3:
        newStatus = AttendanceStatusEnum.PAUSED;
        break;
      case 4:
        newStatus = AttendanceStatusEnum.CLOSED;
        break;
      default:
        newStatus = AttendanceStatusEnum.OVERTIME;
    }
    this.formGroup.get('status').setValue(newStatus, { emitEvent: false });
    this.filterData();
  }

  addContact(): void {
    this.dialog.open(ContactEditComponentForAttendance, {
      //width: '600px'
      width: '800px'
    });
  }

  startNewChat(): void {
    this.dialog.open(ContactNewConversationComponent, {
      // width: '98rem',
      // height: '94rem',
      width: '90vw',
      height: '95vh',
    });
  }

  private clearAttendanceParam(): void {
    if (!!this.idAttendanceParam) {
      this.router.navigate([], {
        queryParams: { id: null, status: null, message: null },
        queryParamsHandling: 'merge',
        replaceUrl: true
      })
      this.idAttendanceParam = null;
    }
  }

  selectAttendance(attendance: Attendance): void {
    this.clearAttendanceParam();

    if (this.attendance?._id !== attendance._id) {
      // Only the assignee clears the unread count (RF-27); a manager opening it leaves it as it is.
      if (!attendance.user || attendance.user._id === super.getIDCurrentUser()) {
        this.adjustUnreadOnOpen(attendance);
        attendance = { ...attendance, countUnreadMessages: 0 };
      }
      this.attendance = attendance;
      this.store.dispatch(setMessages({ messages: new Pager<Message>() }));
      this.store.dispatch(setAttendance({ attendance: this.attendance }));
      this.store.dispatch(openCloseSidebarRight({ sidebarRight: false }));
      this.loadMessageService.setLoadingMessage(true);
    }
  }

  private handleWhisperAlert(alert: AttendanceWhisperAlert): void {
    if (!alert?.attendanceId) {
      return;
    }
    if (alert.action === 'deleted') {
      this.store.dispatch(alert.pending
        ? addWhisperPending({ idAttendance: alert.attendanceId })
        : removeWhisperPending({ idAttendance: alert.attendanceId }));
      return;
    }
    if (this.attendance?._id === alert.attendanceId) {
      this.whisperService.markSeen(alert.attendanceId).pipe(takeUntil(this.destroy$)).subscribe();
      return;
    }
    this.store.dispatch(addWhisperPending({ idAttendance: alert.attendanceId }));

    const audioState = localStorage.getItem('audioState');
    const isAudioOn = audioState !== null ? JSON.parse(audioState) : true;
    if (isAudioOn) {
      this.notificationAudio.play().catch((err) => console.error('[NOTIFICATION] Error playing:', err));
    }
  }

  private checkAndPlayNotificationSound(counts: Array<{ count: number, status: AttendanceStatusEnum }>): void {
    const newOvertimeCount = counts?.find((value) => value.status === AttendanceStatusEnum.OVERTIME)?.count || 0;
    const newPendingCount = counts?.find((value) => value.status === AttendanceStatusEnum.PENDING)?.count || 0;
    const newInProgressCount = counts?.find((value) => value.status === AttendanceStatusEnum.IN_PROGRESS)?.count || 0;
    const newPausedCount = counts?.find((value) => value.status === AttendanceStatusEnum.PAUSED)?.count || 0;

    const oldTotal = this.overtimeCount + this.pendingCount + this.inProgressCount + this.pausedCount;
    const newTotal = newOvertimeCount + newPendingCount + newInProgressCount + newPausedCount;

    const audioState = localStorage.getItem('audioState');
    const isAudioOn = audioState !== null ? JSON.parse(audioState) : true;

    if (isAudioOn && newTotal > oldTotal && oldTotal > 0) {
      this.notificationAudio.play().catch((err) => console.error('[NOTIFICATION] Error playing:', err));
    }
  }

  private setValuesCountsAttendance(counts: Array<CountState>): void {
    this.overtimeCount = counts?.find((value) => value.status === AttendanceStatusEnum.OVERTIME)?.count || 0;
    this.pendingCount = counts?.find((value) => value.status === AttendanceStatusEnum.PENDING)?.count || 0;
    this.inProgressCount = counts?.find((value) => value.status === AttendanceStatusEnum.IN_PROGRESS)?.count || 0;
    this.pausedCount = counts?.find((value) => value.status === AttendanceStatusEnum.PAUSED)?.count || 0;
    this.closedCount = counts?.find((value) => value.status === AttendanceStatusEnum.CLOSED)?.count || 0;
    this.unreadByStatus = Object.fromEntries((counts ?? []).map((value) => [value.status, value.unread ?? 0]));
  }

  unreadOf(status: AttendanceStatusEnum): number {
    return this.unreadByStatus[status] ?? 0;
  }

  unreadLabel(status: AttendanceStatusEnum): string {
    const unread = this.unreadOf(status);
    if (unread <= 0) {
      return '';
    }
    return unread > 99 ? '99+' : String(unread);
  }

  tabAriaLabel(labelKey: string, status: AttendanceStatusEnum, total: number): string {
    return this.t('attendance.panelList.tabSummary', { status: this.t(labelKey), total, unread: this.unreadOf(status) });
  }

  // The tab's badge drops at once for the opener; the server's event then confirms it
  // (SPEC-2026-10-attendance-tabs-unread-messages RF-11).
  private adjustUnreadOnOpen(attendance: Attendance): void {
    const weight = unreadWeight(attendance.countUnreadMessages);
    if (weight > 0) {
      this.store.dispatch(adjustUnreadCount({ status: attendance.status, delta: -weight }));
    }
  }

  showAddContact(): boolean {
    return this.getAddLeadsCurrentUser();
  }

  onScroll(event: Event): void {
    // const buffer = 400;
    // const target = event.target as HTMLElement;
    // if (target.scrollHeight - target.scrollTop <= target.clientHeight + buffer) {
    //   if (this.loading) return;

    //   clearTimeout(this.debounceTimer);
    //   this.debounceTimer = setTimeout(() => {
    //     this.loadMoreContacts();
    //   })
    // }
  }

  loadMoreContacts(): void {
    if (this.pager.list.length < this.pager.total) {
      this.loading = true;
      this.loadingMore = true;
      this.store.dispatch(getAttendances({
        page: this.pager.page + 1,
        perPage: this.pager.perPage,
        search: this.searchString,
        params: this.searchParams
      }));
    }
  }

  /*********************************************************************************
   ***************************** Socket Methods - init *****************************
   *********************************************************************************/

  private attendancesQueue$ = new Subject<Attendance>();
  private socketConnectedStatus$ = new Subject<boolean>();

  private setupAttendancesQueue() {
    this.attendancesQueue$.pipe(
      concatMap((attendance: Attendance) => {
        if (!this.blockSocketWhenFilterIsActive) {

          // reload counts of attendances by status
          this.store.dispatch(getCounts({ search: this.searchString, params: this.searchParams }));

          const selectedStatus = this.formGroup.get('status').value;
          const indexAttendance = this.pager.list?.findIndex((value) => value._id === attendance?._id) ?? -1;
          const indexPinnedAttendance = this.pinnedAttendances.findIndex((value) => value._id === attendance?._id) ?? -1;

          /**
           * Just will call if the status of the attendance is the same as the status selected in the filter,
           * or this attendance is in the current list of attendances or in the pinned attendances,
           * to avoid reload the list when receive an update of an attendance that is in another status,
           * for example, or when receive an update of an attendance that is not in the current list because was in another page of the pagination.
           */
          if (selectedStatus === attendance?.status || indexAttendance >= 0 || indexPinnedAttendance >= 0) {

            let page = this.pager.page;
            let perPage = this.pager.perPage;
            let listSize = this.pager.list.length ?? 0;
            if (listSize > perPage) {
              page = 1;
              perPage = listSize;
            }
            this.store.dispatch(getAttendances({ page, perPage, search: this.searchString, params: this.searchParams }));

            const audioState = localStorage.getItem('audioState');
            const isAudioOn = audioState !== null ? JSON.parse(audioState) : true;
            const currentUserId = this.getIDCurrentUser();
            const assignedUserId = attendance.user?._id;
            if (currentUserId === assignedUserId && isAudioOn) {
              if (!this.attendance || this.attendance._id !== attendance._id) {
                this.notificationAudio.play();
              }
            }
          }
        }
        return of(null);
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  /**
   * Only to refresh messages when socket reconnects
   */
  private setupConnectedStatus() {
    this.socketConnectedStatus$.pipe(
      takeUntil(this.destroy$),
      concatMap((connected) => {
        if (connected) {
          this.store.dispatch(getWhisperPending());
          this.store.dispatch(getCounts({ search: this.searchString, params: this.searchParams }));
          this.store.dispatch(getAttendances({
            page: this.pager.page,
            perPage: this.pager.perPage,
            search: this.searchString,
            params: this.searchParams,
          }));
        }
        return of(null);
      }),
    ).subscribe();
  }

  private configSocket() {
    const code = super.getCodeCompanyCurrentUser();

    this.setupAttendancesQueue();
    this.setupConnectedStatus();

    const sname = `event_attendances_${code}`;
    this.socketService.joinEvent(sname);
    this.socketService.listen(sname)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (attendanceSocketUpdateData: AttendanceSocketUpdateData) => {
          this.attendancesQueue$.next(attendanceSocketUpdateData.attendance);
        }
      });

    // Whisper alerts arrive on the user's own room (ADR-002): indicator plus sound, unless that
    // conversation is already on screen — then it is simply marked seen (RF-11).
    this.socketService.listen(WHISPER_ALERT_SOCKET_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe((alert: AttendanceWhisperAlert) => this.handleWhisperAlert(alert));

    this.socketService.getConnectedStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((connected: boolean) => this.socketConnectedStatus$.next(connected));
  }

  /*********************************************************************************
   ***************************** Socket Methods - end  *****************************
   *********************************************************************************/

}
